import { Component, computed, input } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  // Shell / navigation
  lucideSearch, lucideX, lucideUser, lucideLogOut, lucideBadge,
  lucideZap, lucideHeart, lucideChevronDown, lucideChevronUp,
  lucideChevronLeft, lucideChevronRight, lucideChevronsUpDown, lucideChevronsDown,
  lucideRotateCcw, lucideRefreshCw, lucideBuilding2, lucidePhone,
  lucideUserRound, lucideExpand, lucideMenu, lucideArrowLeft,
  lucideArrowRight, lucideArrowUp, lucideArrowDown,
  // Common actions
  lucidePlus, lucideMinus, lucideTrash2, lucidePencil, lucideCheck,
  lucideCopy, lucideSave, lucideSend, lucideDownload, lucideUpload,
  lucidePrinter, lucideSettings, lucideFilter, lucideSlidersHorizontal,
  lucideInfo, lucideCircleAlert, lucideTriangleAlert, lucideCircleCheck,
  lucideCircleX, lucideCircleHelp, lucideEllipsis, lucideEllipsisVertical,
  lucideExternalLink, lucideLink, lucideEye, lucideEyeOff, lucideStar,
  lucideHouse, lucideCalendar, lucideClock, lucideGlobe, lucideMapPin,
  lucideMail, lucideMessageSquare, lucideBell, lucideBellOff,
  // Status / entities
  lucideCircle, lucideCircleDot, lucideSquare, lucideSquareCheck,
  lucideFileText, lucideFile, lucideFolder, lucideBox, lucideInbox,
  lucideCreditCard, lucideReceipt, lucideCalculator, lucideWallet,
  // Communication
  lucidePhoneCall, lucidePhoneIncoming, lucidePhoneOutgoing,
  lucidePhoneMissed, lucidePhoneForwarded, lucideMessageCircle,
  // User/identity
  lucideUsers, lucideUserCheck, lucideUserX, lucideContact,
  // Misc
  lucideTag, lucideTags, lucideBookmark, lucideFlame, lucideTarget,
  lucideLanguages, lucideFactory, lucideHeadphones, lucideHeadset,
  lucideLayoutGrid, lucideList, lucideLayoutDashboard, lucideLock,
  lucideUnlock, lucideKey, lucideShield, lucideWifi, lucideWifiOff,
  lucidePlay, lucidePause, lucideSkipForward, lucideSkipBack,
  lucideVolume2, lucideVolumeX, lucideMicVocal, lucideMic, lucideMicOff,
  lucidePower, lucidePowerOff, lucideEuro, lucideDollarSign,
  lucidePercent, lucideHash, lucideAtSign,
  lucideLoader, lucideLoaderCircle, lucideCheckCheck,
  lucideArchive, lucideBookOpen, lucideClipboard, lucideClipboardCheck,
  lucideMoon, lucideSun, lucideSparkles, lucideAnchor, lucideTimer, lucideType,
  lucideArrowUpRight, lucideCalendarRange, lucidePhoneOff, lucideScale,
  lucideSearchX, lucideUploadCloud, lucideUserPlus, lucideKeyRound,
  lucideShare2, lucideScrollText, lucideBan, lucideWrench, lucideCalendarPlus, lucideCoins,
} from '@ng-icons/lucide';
import { cn } from './cn';

const ALL_ICONS = {
  lucideSearch, lucideX, lucideUser, lucideLogOut, lucideBadge,
  lucideZap, lucideHeart, lucideChevronDown, lucideChevronUp,
  lucideChevronLeft, lucideChevronRight, lucideChevronsUpDown, lucideChevronsDown,
  lucideRotateCcw, lucideRefreshCw, lucideBuilding2, lucidePhone,
  lucideUserRound, lucideExpand, lucideMenu, lucideArrowLeft,
  lucideArrowRight, lucideArrowUp, lucideArrowDown,
  lucidePlus, lucideMinus, lucideTrash2, lucidePencil, lucideCheck,
  lucideCopy, lucideSave, lucideSend, lucideDownload, lucideUpload,
  lucidePrinter, lucideSettings, lucideFilter, lucideSlidersHorizontal,
  lucideInfo, lucideCircleAlert, lucideTriangleAlert, lucideCircleCheck,
  lucideCircleX, lucideCircleHelp, lucideEllipsis, lucideEllipsisVertical,
  lucideExternalLink, lucideLink, lucideEye, lucideEyeOff, lucideStar,
  lucideHouse, lucideCalendar, lucideClock, lucideGlobe, lucideMapPin,
  lucideMail, lucideMessageSquare, lucideBell, lucideBellOff,
  lucideCircle, lucideCircleDot, lucideSquare, lucideSquareCheck,
  lucideFileText, lucideFile, lucideFolder, lucideBox, lucideInbox,
  lucideCreditCard, lucideReceipt, lucideCalculator, lucideWallet,
  lucidePhoneCall, lucidePhoneIncoming, lucidePhoneOutgoing,
  lucidePhoneMissed, lucidePhoneForwarded, lucideMessageCircle,
  lucideUsers, lucideUserCheck, lucideUserX, lucideContact,
  lucideTag, lucideTags, lucideBookmark, lucideFlame, lucideTarget,
  lucideLanguages, lucideFactory, lucideHeadphones, lucideHeadset,
  lucideLayoutGrid, lucideList, lucideLayoutDashboard, lucideLock,
  lucideUnlock, lucideKey, lucideShield, lucideWifi, lucideWifiOff,
  lucidePlay, lucidePause, lucideSkipForward, lucideSkipBack,
  lucideVolume2, lucideVolumeX, lucideMicVocal, lucideMic, lucideMicOff,
  lucidePower, lucidePowerOff, lucideEuro, lucideDollarSign,
  lucidePercent, lucideHash, lucideAtSign,
  lucideLoader, lucideLoaderCircle, lucideCheckCheck,
  lucideArchive, lucideBookOpen, lucideClipboard, lucideClipboardCheck,
  lucideMoon, lucideSun, lucideSparkles, lucideAnchor, lucideTimer, lucideType,
  lucideArrowUpRight, lucideCalendarRange, lucidePhoneOff, lucideScale,
  lucideSearchX, lucideUploadCloud, lucideUserPlus, lucideKeyRound,
  lucideShare2, lucideScrollText, lucideBan, lucideWrench, lucideCalendarPlus, lucideCoins,
};

@Component({
  selector: 'hlm-icon',
  standalone: true,
  imports: [NgIcon],
  providers: [provideIcons(ALL_ICONS)],
  // Host is inline-flex with leading-none so the element's box matches the
  // SVG exactly — otherwise ng-icon inherits the parent's 1.5 line-height,
  // padding the icon's box and pushing the glyph above the text optical
  // centre in any flex/grid parent (dialog icon-badge, button with icon
  // before text). `_hostClass` is computed so a user-supplied `class` input
  // composes; `shrink-0` prevents the icon from shrinking in tight rows.
  template: `<ng-icon [name]="name()" [size]="size()" class="leading-none" />`,
  host: { '[class]': '_hostClass()' },
})
export class HlmIconComponent {
  readonly name = input.required<string>();
  readonly size = input<string>('1rem');
  readonly userClass = input<string>('', { alias: 'class' });
  protected readonly _hostClass = computed(() =>
    cn('inline-flex shrink-0 items-center justify-center leading-none', this.userClass()),
  );
}

/**
 * Maps legacy Material icon names → Lucide names. Use `matIcon(name)` helper
 * in templates to keep migration diff small.
 */
export const MAT_TO_LUCIDE: Record<string, string> = {
  search: 'lucideSearch', close: 'lucideX', check: 'lucideCheck',
  add: 'lucidePlus', remove: 'lucideMinus', delete: 'lucideTrash2',
  edit: 'lucidePencil', save: 'lucideSave', print: 'lucidePrinter',
  refresh: 'lucideRefreshCw', sync: 'lucideRefreshCw', restart_alt: 'lucideRotateCcw',
  settings: 'lucideSettings', more_vert: 'lucideEllipsisVertical',
  more_horiz: 'lucideEllipsis', arrow_back: 'lucideArrowLeft',
  arrow_forward: 'lucideArrowRight', expand_more: 'lucideChevronDown',
  expand_less: 'lucideChevronUp', chevron_left: 'lucideChevronLeft',
  chevron_right: 'lucideChevronRight', unfold_more: 'lucideChevronsUpDown',
  error: 'lucideCircleX', error_outline: 'lucideCircleAlert',
  warning: 'lucideTriangleAlert', info: 'lucideInfo', help: 'lucideCircleHelp',
  check_circle: 'lucideCircleCheck', cancel: 'lucideCircleX', done: 'lucideCheck',
  business: 'lucideBuilding2', person: 'lucideUserRound', people: 'lucideUsers',
  account_circle: 'lucideUser', badge: 'lucideBadge', contacts: 'lucideContact',
  phone: 'lucidePhone', call: 'lucidePhoneCall', email: 'lucideMail',
  message: 'lucideMessageSquare', notifications: 'lucideBell',
  notifications_off: 'lucideBellOff', forward: 'lucidePhoneForwarded',
  description: 'lucideFileText', insert_drive_file: 'lucideFile',
  folder: 'lucideFolder', inventory: 'lucideBox', inbox: 'lucideInbox',
  receipt: 'lucideReceipt', credit_card: 'lucideCreditCard',
  calculate: 'lucideCalculator', payments: 'lucideWallet',
  logout: 'lucideLogOut', login: 'lucideUser', open_in_new: 'lucideExternalLink',
  link: 'lucideLink', visibility: 'lucideEye', visibility_off: 'lucideEyeOff',
  star: 'lucideStar', home: 'lucideHouse', date_range: 'lucideCalendar',
  event: 'lucideCalendar', schedule: 'lucideClock', language: 'lucideLanguages',
  public: 'lucideGlobe', place: 'lucideMapPin', download: 'lucideDownload',
  upload: 'lucideUpload', send: 'lucideSend', copy: 'lucideCopy',
  content_copy: 'lucideCopy', filter_list: 'lucideFilter', tune: 'lucideSlidersHorizontal',
  menu: 'lucideMenu', bolt: 'lucideZap', favorite: 'lucideHeart',
  local_fire_department: 'lucideFlame', label: 'lucideTag', sell: 'lucideTag',
  lock: 'lucideLock', lock_open: 'lucideUnlock', vpn_key: 'lucideKey',
  shield: 'lucideShield', power_settings_new: 'lucidePower',
  euro: 'lucideEuro', euro_symbol: 'lucideEuro', percent: 'lucidePercent',
  tag: 'lucideHash', alternate_email: 'lucideAtSign',
  loading: 'lucideLoaderCircle', autorenew: 'lucideLoaderCircle',
  archive: 'lucideArchive', menu_book: 'lucideBookOpen',
  assignment: 'lucideClipboard', task_alt: 'lucideClipboardCheck',
  dark_mode: 'lucideMoon', light_mode: 'lucideSun', auto_awesome: 'lucideSparkles',
};

export function matIcon(matName: string): string {
  return MAT_TO_LUCIDE[matName] || 'lucideCircleHelp';
}
